import { useAuth } from '@/contexts/AuthContext'
import { useCompanyAccess } from '@/hooks/useCompanyAccess'
import { auth } from '@/lib/firebase'
import { signOut } from 'firebase/auth'
import { AlertCircle, LogOut } from 'lucide-react'
import { useRouter } from 'next/navigation'
import { Button } from '../ui/button'

interface DisabledCompanyMessageProps {
  message?: string
  showHomeButton?: boolean
}

export function DisabledCompanyMessage({ message, showHomeButton = true }: DisabledCompanyMessageProps) {
  const { userData, setCurrentUser } = useAuth()
  const { isOwner, disabledMessage } = useCompanyAccess()
  const router = useRouter()

  const isSuperAdmin = userData?.userType === 'superAdmin' || userData?.isRoot

  // Use the message from props or from the hook
  const displayMessage = message || disabledMessage

  const handleLogout = async () => {
    try {
      await signOut(auth)
      setCurrentUser(null)
      router.push('/signin')
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  return (
    <div className='fixed inset-0 flex items-center justify-center p-4 bg-neutral-50 z-50'>
      <div className='bg-red-50 border border-red-200 rounded-lg p-6 max-w-lg w-full text-center'>
        <div className='flex items-center justify-center mb-4'>
          <AlertCircle className='h-8 w-8 text-red-500 mr-2' />
          <h2 className='text-xl font-semibold text-red-700'>Company Disabled</h2>
        </div>

        <p className='text-gray-700 mb-6'>{displayMessage}</p>

        <div className='flex flex-col sm:flex-row gap-3 justify-center'>
          {showHomeButton && (
            <Button onClick={() => router.push('/dashboard')} variant='outline'>
              Return to Home
            </Button>
          )}

          {isSuperAdmin && (
            <Button onClick={() => router.push('/companies')} variant='default'>
              Manage Companies
            </Button>
          )}

          {isOwner && (
            <Button onClick={() => router.push('/support')} variant='default'>
              Contact Support
            </Button>
          )}

          <Button onClick={handleLogout} variant='outline' className='gap-2'>
            <LogOut className='h-4 w-4' />
            Logout
          </Button>
        </div>
      </div>
    </div>
  )
}
